import { ACTIVE_STATE_LABEL, TIP, TX_TYPE_LABEL } from '../terms';
import { formatDuration } from '../time';
import { Cell, Chip, type Column, EmptyState, Row, Table } from '../ui';
import type { ActiveRow } from './active';
import { elapsedTone, TONE_BAR, TONE_LABEL, TONE_TEXT, TX_TYPE_TONE } from './format';

/** 경과 시간 막대의 기준 길이. 이 값을 넘으면 막대가 꽉 찬다 */
const BAR_FULL_MS = 10_000;

const COLUMNS: readonly Column[] = [
  { label: '요청 경로', title: TIP.entry },
  { label: '유형', width: 'w-28' },
  { label: '상태', width: 'w-32' },
  { label: '경과시간', width: 'w-44' },
  { label: '현재 구간', width: 'w-44' },
  { label: '회사(테넌트)', width: 'w-28', title: TIP.tenant },
  { label: '인스턴스', width: 'w-48', title: TIP.instance },
];

/**
 * 지금 도는 요청 목록. 오래 걸린 것부터 위에 오고, 오래 걸릴수록 색이 뜨거워진다.
 * 정렬·평탄화는 `active.ts`가 했다.
 */
export function ActiveTable({
  rows,
  filtered,
  showService = false,
}: {
  rows: readonly ActiveRow[];
  filtered?: boolean;
  showService?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title={filtered ? '3초 이상 걸린 요청이 없다' : '지금 처리 중인 요청이 없다'}
        hint={
          filtered
            ? '필터를 끄면 진행 중 요청 전부를 본다'
            : [
                '요청은 대부분 1초 안에 끝난다.',
                '이 목록은 1초마다 찍는 그 순간에 아직 응답이 안 나간 요청만 보인다',
              ]
        }
      />
    );
  }

  return (
    <Table
      columns={showService ? [{ label: '서비스', width: 'w-40' }, ...COLUMNS] : COLUMNS}
      caption="진행 중 요청"
      minWidth="min-w-[980px]"
    >
      {rows.map((row) => {
        const tone = elapsedTone(row.elapsedMs);
        const ratio = Math.min(1, row.elapsedMs / BAR_FULL_MS);
        return (
          <Row key={`${row.instance} ${row.txId}`}>
            {showService ? (
              <Cell title={row.service} className="max-w-[180px] truncate">
                {row.service}
              </Cell>
            ) : null}
            <Cell mono title={row.entry} className="max-w-[320px] truncate">
              {row.entry}
            </Cell>
            <Cell>
              <Chip tone={TX_TYPE_TONE[row.txType]}>{TX_TYPE_LABEL[row.txType]}</Chip>
            </Cell>
            <Cell tone="muted">{ACTIVE_STATE_LABEL[row.state]}</Cell>
            <Cell nowrap>
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-band-strong"
                >
                  <span
                    className={`block h-full rounded-full ${TONE_BAR[tone]}`}
                    style={{ width: `${ratio * 100}%` }}
                  />
                </span>
                <span className={`tabular font-mono ${TONE_TEXT[tone]}`}>
                  {formatDuration(row.elapsedMs)}
                </span>
                {TONE_LABEL[tone] ? (
                  <span className={TONE_TEXT[tone]}>{TONE_LABEL[tone]}</span>
                ) : null}
              </span>
            </Cell>
            <Cell mono tone="muted" title={row.currentSpan} className="max-w-[180px] truncate">
              {row.currentSpan ?? '-'}
            </Cell>
            <Cell tone="muted" className="tabular">
              {row.tenantId ?? '-'}
            </Cell>
            <Cell tone="muted" title={row.instance} className="max-w-[200px] truncate">
              {row.instance}
            </Cell>
          </Row>
        );
      })}
    </Table>
  );
}
